import { create } from "zustand";
import { teamsAPI } from "../services/api";

export interface Workspace {
  id: string;
  name: string;
  description?: string;
  color: string;
  memberCount: number;
  isActive: boolean;
  owner_id: string;
  created_at: string;
}

interface WorkspaceState {
  workspaces: Workspace[];
  activeWorkspaceId: string | null;
  isLoading: boolean;

  fetchWorkspaces: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  createWorkspace: (data: { name: string; description?: string; color?: string }) => Promise<Workspace>;
  updateWorkspace: (workspaceId: string, data: { name?: string; description?: string; color?: string }) => Promise<void>;
  deleteWorkspace: (workspaceId: string) => Promise<void>;
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  workspaces: [],
  activeWorkspaceId: null,
  isLoading: false,

  fetchWorkspaces: async () => {
    set({ isLoading: true });
    try {
      const { data } = await teamsAPI.listWorkspaces();
      set({
        workspaces: data.workspaces,
        activeWorkspaceId: data.active_workspace_id,
        isLoading: false,
      });
    } catch {
      set({ isLoading: false });
    }
  },

  switchWorkspace: async (workspaceId: string) => {
    const { data } = await teamsAPI.switchWorkspace(workspaceId);
    set((state) => ({
      activeWorkspaceId: workspaceId,
      workspaces: state.workspaces.map((ws) => ({
        ...ws,
        isActive: ws.id === workspaceId,
      })),
    }));
    return data;
  },

  createWorkspace: async (reqData) => {
    const { data } = await teamsAPI.createWorkspace(reqData);
    const newWs = data.workspace as Workspace;
    set((state) => ({
      workspaces: [
        ...state.workspaces.map((ws) => ({ ...ws, isActive: false })),
        { ...newWs, isActive: true },
      ],
      activeWorkspaceId: newWs.id,
    }));
    return newWs;
  },

  updateWorkspace: async (workspaceId, reqData) => {
    const { data } = await teamsAPI.updateWorkspace(workspaceId, reqData);
    const updated = data.workspace as Workspace;
    set((state) => ({
      workspaces: state.workspaces.map((ws) =>
        ws.id === workspaceId ? { ...ws, ...updated } : ws
      ),
    }));
  },

  deleteWorkspace: async (workspaceId) => {
    await teamsAPI.deleteWorkspace(workspaceId);
    // Refetch to get the updated list and new active workspace
    await get().fetchWorkspaces();
  },
}));
